import { DropdownMenu } from "common/components/DropdownMenu";
import { ActionButton, MoreActionButton } from "common/components/Table/Tr";
import { EmptyWrapper } from "common/helpers/Components";
import React from "react";
import { useTracking } from "react-tracking";
import magic_wand from "assets/images/magic_wand.svg";

interface ActionButtonProps {
  button: ActionButton;
}

/**
 * Kind-independent styling for a row-action icon (shape, size, padding, border, focus ring). Shared
 * so non-table surfaces that render the same row-action icons — e.g. the inspection item's
 * camera/upload actions — stay visually identical to the document/photo rows and can't drift. Per
 * kind, compose this with the bg/text tone (neutral `bg-secondary-100 text-secondary-800`, or the
 * `delete` error tone) as `ActionButtonView` does below.
 */
export const actionIconBaseClassName =
  "material-symbols-outlined rounded-3xl text-lg p-xs border border-secondary-100 focus:ring-secondary-50 focus:outline-none";

export const ActionButtonView: React.FC<ActionButtonProps> = ({ button }) => {
  // Hooks
  const { trackEvent } = useTracking();

  // Methods
  const actionButtonBgClass = (kind: ActionButton["kind"]): string => {
    return kind === "delete" ? "bg-error-50" : "bg-secondary-100 hover:bg-secondary-50";
  };

  const actionButtonTextClass = (kind: ActionButton["kind"]): string => {
    return kind === "delete" ? "text-error-500" : "text-secondary-800";
  };

  const actionButtonDisabledClass = (disabled: ActionButton["disabled"]): string => {
    return disabled ? "cursor-not-allowed" : "cursor-pointer";
  };

  const onClickWithTrack = (event: React.MouseEvent<HTMLSpanElement>) => {
    if (!button.disabled) {
      trackEvent({
        action: button.analyticsAction || button.kind,
        ...button.analyticsData,
      });

      // Stop the click from bubbling to a clickable Tr's row onClick (action buttons act on their
      // own; they must not also trigger whole-row activation). The "more" button is wrapped by
      // DropdownMenu, which relies on the click bubbling to its own wrapper to toggle — so let it
      // through; DropdownMenu stops propagation at its wrapper before it reaches the row.
      if (button.kind !== "more") {
        event.stopPropagation();
      }

      if (button.onClick) button.onClick();
    }
  };

  // Constants
  const icon = (() => {
    switch (button.kind) {
      case "more":
        return "more_horiz";
      case "magic_wand":
        return <img src={magic_wand} width={16} height={16} />;
      case "import":
        return "system_update_alt";
      default:
        return button.kind;
    }
  })();
  const DropdownWrapper =
    button.kind === "more"
      ? ({ children }: { children: React.ReactNode }) => (
          <DropdownMenu options={(button as MoreActionButton).actions}>{children}</DropdownMenu>
        )
      : EmptyWrapper;

  return (
    <DropdownWrapper>
      <span
        role="button"
        tabIndex={button.disabled ? -1 : 0}
        aria-label={button.kind}
        aria-disabled={button.disabled}
        className={`${actionIconBaseClassName}
                        ${actionButtonBgClass(button.kind)}
                        ${actionButtonTextClass(button.kind)}
                        ${actionButtonDisabledClass(button.disabled)}`}
        onClick={onClickWithTrack}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClickWithTrack(e as unknown as React.MouseEvent<HTMLSpanElement>);
          }
        }}>
        {icon}
      </span>
    </DropdownWrapper>
  );
};
